/*
 * Próximos vencimientos de los clientes (Dashboard del productor).
 *
 * Junta dos fuentes en una sola lista de eventos `{ fecha, nroComitente, cliente,
 * tipo, instrumento, concepto, bucket, monto, montoNominal, parcial, nota }`:
 *   - lo que proyecta el SGC (`proximosVencimientos`, ver
 *     functions/acreditacionesEsperadas.js): cupones, amortizaciones, letras y
 *     T-Bills, cheques y pagarés MAV;
 *   - las cauciones y pases tomadores de la planilla, que el cliente tiene que
 *     devolver al vencimiento.
 *
 * Todo es informativo: no entra en ningún saldo.
 */
import { MONEDA_BUCKETS, bucketDeMoneda } from '../constants/monedas';
import { toIsoDate } from './dateHelpers';
import { formatNumberForDisplay } from './formatters';

export const HORIZONTES_DIAS = [7, 30, 60, 90];
export const HORIZONTE_DEFAULT = 30;

export const TIPO_CAUCION = 'caucion';
export const TIPO_PASE = 'pase';

export const SENTIDO_TODOS = 'todos';
export const SENTIDO_COBROS = 'cobros';
export const SENTIDO_PAGOS = 'pagos';

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 'YYYY-MM-DD' + `dias` días de calendario. */
export const sumarDiasIso = (iso, dias) => {
    const [y, m, d] = String(iso).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + dias)).toISOString().slice(0, 10);
};

const numeroONull = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/** Docs de `proximosVencimientos` (uno por comitente) → eventos planos. */
export const eventosDesdeSgc = (docs = []) => docs.flatMap((doc) => {
    const nroComitente = String(doc.nroComitente || '');
    return (Array.isArray(doc.eventos) ? doc.eventos : [])
        .filter(ev => ISO_DAY_RE.test(String(ev?.fecha || '')))
        .map((ev, index) => ({
            id: `sgc-${ev.eventId || `${nroComitente}-${index}`}`,
            fecha: ev.fecha,
            nroComitente,
            cliente: doc.nombre || '',
            tipo: ev.tipo,
            instrumento: ev.instrumento || '',
            concepto: ev.concepto || '',
            bucket: ev.bucket,
            monto: numeroONull(ev.monto),
            montoNominal: numeroONull(ev.montoNominal),
            monedaNominal: ev.monedaNominal || null,
            montoEstimado: numeroONull(ev.montoEstimado),
            tipoCambioEstimado: ev.tipoCambioEstimado || null,
            parcial: ev.parcial === true,
            nota: ev.nota || null,
            fechaOriginal: ev.fechaOriginal || null,
        }));
});

const fechaIsoDe = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : toIsoDate(value);
    if (typeof value?.toDate === 'function') return toIsoDate(value.toDate());
    return null;
};

/**
 * Cauciones y pases tomadores (archivados en `vencimientos` o en vivo, ver
 * vencimientosFinanciacionEnVivo) → eventos. El cliente devuelve el capital más
 * los intereses: el monto es el capital, un piso (`parcial`).
 */
export const eventosDeFinanciacion = (vencimientos = []) => vencimientos.map((v) => {
    const fecha = fechaIsoDe(v.fechaVencimiento);
    const capital = parseFloat(v.montoOperado) || 0;
    if (!fecha || capital <= 0) return null;
    const esPase = v.tipo === 'Pase';
    const dias = Number(v.dias) || 0;
    const tasa = parseFloat(v.tasaOperada) || parseFloat(v.tasa) || 0;
    return {
        id: `fin-${v.id}`,
        fecha,
        nroComitente: String(v.nroComitente || ''),
        cliente: v.cliente || '',
        tipo: esPase ? TIPO_PASE : TIPO_CAUCION,
        instrumento: esPase ? 'Pase' : `Caución${dias ? ` a ${dias} días` : ''}`,
        concepto: esPase ? 'Pase tomador (vence)' : 'Caución tomadora (vence)',
        bucket: bucketDeMoneda(v.moneda),
        monto: -capital,
        montoNominal: null,
        parcial: true,
        nota: `Capital tomado: al vencimiento se suman los intereses${tasa ? ` (TNA ${formatNumberForDisplay(tasa)} %)` : ''}.`,
        fechaOriginal: null,
    };
}).filter(Boolean);

/**
 * Recorta los eventos a las cuentas de `comitentes`; null = sin recorte. Hace falta
 * en el staff (comercial con alcance): sus cauciones y pases del día vienen de toda
 * la casa.
 */
export const acotarAComitentes = (eventos, comitentes) => {
    if (!Array.isArray(comitentes)) return eventos;
    const permitidos = new Set(comitentes.map(String));
    return eventos.filter(ev => permitidos.has(String(ev.nroComitente)));
};

/** Eventos de hoy a hoy + `dias`, ordenados por fecha y cliente. */
export const filtrarHorizonte = (eventos, hoyIso, dias) => {
    const hasta = sumarDiasIso(hoyIso, dias);
    return eventos
        .filter(ev => ev.fecha >= hoyIso && ev.fecha <= hasta)
        .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.cliente.localeCompare(b.cliente, 'es'));
};

// Un dollar linked no tiene monto en pesos: su sentido sale del nominal.
const valorConSigno = (ev) => ev.monto ?? ev.montoNominal ?? 0;

/** Filtros de la tabla: moneda (bucket o null), cobros/pagos y texto libre. */
export const filtrarEventos = (eventos, { moneda = null, sentido = SENTIDO_TODOS, busqueda = '' } = {}) => {
    const termino = busqueda.trim().toLowerCase();
    return eventos.filter((ev) => {
        if (moneda && ev.bucket !== moneda) return false;
        if (sentido === SENTIDO_COBROS && valorConSigno(ev) < 0) return false;
        if (sentido === SENTIDO_PAGOS && valorConSigno(ev) >= 0) return false;
        if (!termino) return true;
        return [ev.cliente, ev.nroComitente, ev.instrumento, ev.concepto]
            .some(campo => String(campo || '').toLowerCase().includes(termino));
    });
};

/**
 * Totales por moneda, en el orden canónico (Pesos, Dólares, USD Cable). Los dollar
 * linked no suman al neto (su importe en pesos depende del A3500 del día de pago):
 * van aparte, con su nominal en dólares y su estimado en pesos al mayorista.
 */
export const resumirPorMoneda = (eventos) => MONEDA_BUCKETS.map((bucket) => {
    const deMoneda = eventos.filter(ev => ev.bucket === bucket);
    let cobros = 0;
    let pagos = 0;
    let linkedNominal = 0;
    let linkedCantidad = 0;
    let linkedEstimado = 0;
    let linkedSinEstimar = 0;
    deMoneda.forEach((ev) => {
        if (typeof ev.monto === 'number') {
            if (ev.monto >= 0) cobros += ev.monto;
            else pagos += ev.monto;
        } else if (typeof ev.montoNominal === 'number') {
            linkedNominal += ev.montoNominal;
            linkedCantidad += 1;
            if (typeof ev.montoEstimado === 'number') linkedEstimado += ev.montoEstimado;
            else linkedSinEstimar += 1;
        }
    });
    return {
        bucket,
        cantidad: deMoneda.length,
        cobros,
        pagos,
        neto: cobros + pagos,
        hayParcial: deMoneda.some(ev => ev.parcial),
        linkedNominal,
        linkedCantidad,
        linkedEstimado,
        linkedSinEstimar,
    };
});
